import { useEffect, useState } from 'react';
import { useCallStore } from '@/stores/callStore';
import { useCategoryStore } from '@/stores/categoryStore';
import { cn } from '@/lib/utils';

export function VoiceCallPage() {
  const {
    preferences,
    session,
    onlineCount,
    isLoading,
    isMuted,
    isSpeakerOn,
    error,
    loadPreferences,
    updatePreferences,
    verifyAge,
    joinPool,
    leavePool,
    startSearch,
    cancelSearch,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    clearError,
    cleanup,
  } = useCallStore();

  const { categories, categoriesLoading, loadCategories } = useCategoryStore();
  const [showAgeDialog, setShowAgeDialog] = useState(false);
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [searchSeconds, setSearchSeconds] = useState(0);

  useEffect(() => {
    loadPreferences();
    loadCategories();
    return () => cleanup();
  }, [loadPreferences, loadCategories, cleanup]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Search timer
  useEffect(() => {
    if (session.status === 'searching') {
      setSearchSeconds(0);
      const interval = setInterval(() => setSearchSeconds((s) => s + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [session.status]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerifyAge = async () => {
    if (!birthMonth || !birthDay || !birthYear || !ageConfirmed) return;

    const birth = new Date(parseInt(birthYear), parseInt(birthMonth) - 1, parseInt(birthDay));
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    if (age < 18) {
      useCallStore.setState({ error: 'You must be 18 or older to use this feature' });
      return;
    }

    const dateStr = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
    await verifyAge(dateStr);
    setShowAgeDialog(false);
  };

  // Age verification required
  if (!preferences.isAdultVerified && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <svg className="w-16 h-16 text-red-500 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <h1 className="text-2xl font-semibold mb-4">Age Verification Required</h1>
        <p className="text-muted-foreground text-center mb-8 max-w-md">
          This feature is only available for users 18 years and older. Please verify your age to continue.
        </p>
        <button
          onClick={() => setShowAgeDialog(true)}
          className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity"
        >
          Verify Age
        </button>

        {/* Age verification dialog */}
        {showAgeDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-xl border border-border">
              <h2 className="text-lg font-semibold mb-2">Age Verification Required</h2>
              <p className="text-sm text-muted-foreground mb-6">
                To use the voice call feature, please confirm your date of birth. This feature is for users 18 years and older.
              </p>

              <label className="text-sm font-medium mb-2 block">Date of Birth</label>
              <div className="flex gap-2 mb-4">
                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Month</option>
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                    <option key={m} value={String(i + 1)}>{m}</option>
                  ))}
                </select>
                <select
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  className="w-20 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                  ))}
                </select>
                <select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  className="w-24 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Year</option>
                  {Array.from({ length: 100 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return <option key={year} value={String(year)}>{year}</option>;
                  })}
                </select>
              </div>

              <label className="flex items-center gap-2 text-sm mb-4">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  className="rounded"
                />
                I confirm I am 18 years or older
              </label>

              <div className="bg-red-500/10 text-red-500 text-sm p-3 rounded-lg flex gap-2 mb-6">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>This setting cannot be changed later.</span>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowAgeDialog(false)}
                  className="px-4 py-2 text-sm rounded-lg border border-input hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyAge}
                  disabled={!birthMonth || !birthDay || !birthYear || !ageConfirmed}
                  className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  Confirm Age
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Searching / ringing overlay
  if (session.status === 'searching' || session.status === 'ringing') {
    const isRinging = session.status === 'ringing';
    return (
      <div className="fixed inset-0 bg-background/95 flex flex-col items-center justify-center z-50">
        <div className="relative w-48 h-48 mb-8">
          <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" />
          <div className="absolute inset-4 rounded-full border-2 border-primary/50 animate-ping" style={{ animationDelay: '200ms' }} />
          <div className={cn(
            'absolute inset-8 rounded-full flex items-center justify-center',
            isRinging ? 'bg-green-500/20' : 'bg-muted'
          )}>
            {isRinging ? (
              <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            ) : (
              <svg className="w-12 h-12 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-2">
          {isRinging ? 'Incoming Call' : 'Finding someone to talk...'}
        </h2>

        {!isRinging && (
          <p className="text-muted-foreground mb-8 font-mono">{formatTime(searchSeconds)}</p>
        )}

        {isRinging && session.sessionId ? (
          <div className="flex gap-6">
            <button
              onClick={() => declineCall(session.sessionId!)}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
              </svg>
            </button>
            <button
              onClick={() => acceptCall(session.sessionId!)}
              className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            onClick={cancelSearch}
            className="px-6 py-2 rounded-lg border border-input hover:bg-muted transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    );
  }

  // Active call
  if (session.status === 'active') {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center z-50">
        <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mb-6">
          <svg className="w-12 h-12 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold mb-2">Stranger</h2>
        <p className="text-muted-foreground mb-4">Connected</p>
        <p className="text-4xl font-light font-mono mb-12 tabular-nums">{formatTime(session.durationSeconds)}</p>

        <div className="flex gap-6">
          <button
            onClick={toggleMute}
            className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center transition-colors',
              isMuted ? 'bg-foreground text-background' : 'bg-muted text-foreground'
            )}
          >
            {isMuted ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            )}
          </button>
          <button
            onClick={toggleSpeaker}
            className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center transition-colors',
              isSpeakerOn ? 'bg-foreground text-background' : 'bg-muted text-foreground'
            )}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          </button>
          <button
            onClick={endCall}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Main preferences screen
  const isInPool = session.status === 'inPool';
  const isAvailable = preferences.status === 'available';

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold">Voice Call</h1>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-sm">
          {error}
        </div>
      )}

      {isLoading || categoriesLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {/* Preferences */}
          <div className="space-y-4 bg-card p-6 rounded-lg border border-border">
            {/* Status */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <select
                value={preferences.status}
                onChange={(e) => updatePreferences({ status: e.target.value as 'available' | 'offline' })}
                disabled={isInPool}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value="available">Available</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <label className="text-sm font-medium">I am</label>
              <select
                value={preferences.gender || ''}
                onChange={(e) => updatePreferences({ gender: e.target.value || null })}
                disabled={isInPool}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value="">Not specified</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="Mx">Non-binary</option>
                <option value="ND">Do not disclose</option>
              </select>
            </div>

            {/* Seek gender */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Connect with</label>
              <select
                value={preferences.seekGender}
                onChange={(e) => updatePreferences({ seekGender: e.target.value as 'M' | 'F' | 'Mx' | 'everyone' })}
                disabled={isInPool}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value="everyone">Everyone</option>
                <option value="M">Men</option>
                <option value="F">Women</option>
                <option value="Mx">Non-binary</option>
              </select>
            </div>

            {/* Interests */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Interests (select up to 10)</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const isSelected = preferences.interests.includes(cat.slug);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        if (isInPool) return;
                        const newInterests = isSelected
                          ? preferences.interests.filter((i: string) => i !== cat.slug)
                          : preferences.interests.length < 10
                          ? [...preferences.interests, cat.slug]
                          : preferences.interests;
                        updatePreferences({ interests: newInterests });
                      }}
                      disabled={isInPool}
                      className={cn(
                        'px-3 py-1 text-sm rounded-full border transition-colors',
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-input hover:bg-muted',
                        isInPool && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mood */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Mood (optional)</label>
              <select
                value={preferences.mood || ''}
                onChange={(e) => updatePreferences({ mood: e.target.value || null })}
                disabled={isInPool}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value="">Any mood</option>
                <option value="casual">Just chatting</option>
                <option value="deep_talk">Deep conversation</option>
                <option value="advice">Seeking advice</option>
                <option value="venting">Need to vent</option>
                <option value="fun">Having fun</option>
              </select>
            </div>
          </div>

          {/* Online count */}
          <div className="flex items-center justify-center gap-2 p-4 bg-muted rounded-lg">
            <span className={cn('w-2 h-2 rounded-full', onlineCount > 0 ? 'bg-green-500' : 'bg-gray-500')} />
            <span className="text-sm">{onlineCount} {onlineCount === 1 ? 'person' : 'people'} online</span>
          </div>

          {/* Action buttons */}
          {!isAvailable ? (
            <div className="space-y-2">
              <button disabled className="w-full py-3 rounded-lg bg-primary text-primary-foreground opacity-50 cursor-not-allowed font-medium">
                Set status to Available
              </button>
              <p className="text-center text-sm text-muted-foreground">
                Change your status to "Available" to connect with others
              </p>
            </div>
          ) : !isInPool ? (
            <button
              onClick={joinPool}
              className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
            >
              Go Online
            </button>
          ) : (
            <div className="space-y-3">
              <button
                onClick={startSearch}
                className="w-full py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium transition-colors"
              >
                Connect
              </button>
              <button
                onClick={leavePool}
                className="w-full py-3 rounded-lg border border-input hover:bg-muted font-medium transition-colors"
              >
                Go Offline
              </button>
            </div>
          )}

          {/* Disclaimer */}
          <p className="text-xs text-muted-foreground text-center">
            By using this feature, you agree to our Community Guidelines.
            Be respectful and report any inappropriate behavior.
          </p>
        </>
      )}
    </div>
  );
}
